/**
 * Shared ApiClient for the admin console.
 *
 * Auth (Checkpoint #4): when the VITE_AUTH0_* env vars are present, staff sign
 * in via Auth0 SSO and the Auth0 access token is the API bearer (see lib/auth).
 * Without them (local dev) the app falls back to a pasted backend session JWT —
 * mint one with `scripts/mint-dev-session-jwt.py` (super_admin / brokerage_admin)
 * or copy it from a logged-in web session — entered in the sidebar token field.
 */

import { ApiClient } from "@re-platform/api-client";

import { actingTenantId } from "./actingTenant";
import { getApiToken } from "./auth";

const TOKEN_KEY = "rp.admin.token";

export function loadAdminToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function saveAdminToken(token: string): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // localStorage unavailable — the next request will just go out anonymous.
  }
}

// Default to the page origin: the vite dev server proxies /admin, /document,
// /mortgage to the per-service local ports (see vite.config.ts). Cloud builds
// set VITE_API_BASE_URL to the HTTP API gateway explicitly.
const baseUrl =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  window.location.origin;

export const api = new ApiClient({
  baseUrl,
  getToken: async () => getApiToken(loadAdminToken),
  getActingTenantId: actingTenantId,
  timeoutMs: 30_000,
});
